/**
 * GA4 event name constants — single source of truth.
 * Never hardcode these strings at call sites; always import from here.
 *
 * [GA4 recommended] = GA4 recognises these names natively; built-in funnel
 *                     and e-commerce reports are built around them. Do NOT rename.
 * [Custom]          = defined by us; GA4 stores them as regular events.
 *
 * `select_item` and `view_item` fire alone, carrying `game_id` / `game_name` as flat params.
 */
export const ANALYTICS_EVENTS = {
  // [GA4 recommended] Fires on every Angular route change.
  // Handled automatically by AnalyticsService via NavigationEnd. Do not call manually.
  PAGE_VIEW: 'page_view',

  // [GA4 recommended] The events list was shown to the user.
  // Call trackViewItemList() from event-landing.component.ts on init and after filters change.
  VIEW_ITEM_LIST: 'view_item_list',

  // [GA4 recommended] The user clicked an event card from the list. Also sets the journey origin.
  // Call trackSelectItem() from sale-event.component.ts on card click.
  SELECT_ITEM: 'select_item',

  // [GA4 recommended] The user is viewing the seat-selection page for a specific event.
  // Call trackViewItem() from select-seat.component.ts once the event + availability load.
  VIEW_ITEM: 'view_item',

  // [GA4 recommended] Seats were added to the cart with user intent (not a synthetic
  // tutorial/cart-restorer mutation). One event per mutation, `quantity` = seats added.
  // Call trackAddToCart() from seat-management.service.ts.
  ADD_TO_CART: 'add_to_cart',

  // [GA4 recommended] Seats were removed from the cart with user intent.
  // Call trackRemoveFromCart() from seat-management.service.ts.
  REMOVE_FROM_CART: 'remove_from_cart',

  // [GA4 recommended] POST /api/transaction succeeded — the seats are locked, checkout begins.
  // Call trackBeginCheckout() from total.component.ts goToCheckout() success.
  BEGIN_CHECKOUT: 'begin_checkout',

  // [GA4 recommended] The user began filling the checkout form — first dirty valueChanges.
  // GA4 only auto-fires this for enhanced-measurement forms, which this one is not.
  FORM_START: 'form_start',

  // [Custom] Fixed companion event to FORM_START.
  // Kept as its own constant so a report can select it without a `contains`
  // filter on FORM_START.
  FORM_START_CHECKOUT: 'form_start_checkout',

  // [GA4 recommended] The checkout page's address/contact fields are complete.
  // GA4's built-in Checkout journey has a fixed "Add shipping" step keyed on this exact name;
  // GS has no shipping, but skipping it would zero every step after it (Add payment, Purchase).
  // Call trackAddShippingInfo() from checkout.component.ts submitPayment(), once the
  // address/phone/email completeness checks pass.
  ADD_SHIPPING_INFO: 'add_shipping_info',

  // [GA4 recommended] The user authenticated via Okta (or demo sign-in).
  // Call trackLogin() from okta-integration.service.ts.
  LOGIN: 'login',

  // [Custom] The user explicitly signed out. Fired synchronously from the signOut() entry
  // point, NOT from an auth-state transition: the Okta end-session redirect tears the page
  // down before the transition-based hit could leave, and the post-return instance never
  // observes a true->false change. The button is only rendered while authenticated and the
  // sign-out always follows the click, so the click itself is the reliable trigger.
  // Call trackLogout() from okta-integration.service.ts signOut().
  LOGOUT: 'logout',

  // [Custom] The user changed the transaction's buyer type onto an upsell via the
  // editTransactionBuyerType() PATCH — a real, API-synced add-on (distinct from "upgrade",
  // which is client-side-only gamification, see UPGRADE_SHOWN/UPGRADE_ACQUIRED below).
  // Call trackUpsellSelected() from checkout.component.ts editBuyerType() success.
  UPSELL_SELECTED: 'upsell_selected',

  // [Custom] The inverse of UPSELL_SELECTED: the buyer-type PATCH moved the transaction
  // back off an upsell onto the event's default buyer type. Same payload shape, keyed on
  // the upsell being left — without it a deselected add-on would stay "selected" forever
  // in the reporting.
  // Call trackUpsellDeselected() from checkout.component.ts editBuyerType() success.
  UPSELL_DESELECTED: 'upsell_deselected',

  // [Custom] The rainout insurance toggle was added or removed.
  // Call trackRainoutInsuranceToggled() from checkout.component.ts onRainoutChange().
  RAINOUT_INSURANCE_TOGGLED: 'rainout_insurance_toggled',

  // [Custom] A discount code was applied successfully. Never carries the code string itself,
  // private/group-link codes must not reach GA4, only the resulting discount amount.
  // Call trackDiscountApplied() from discount-code.component.ts apply() success.
  DISCOUNT_APPLIED: 'discount_applied',

  // [GA4 recommended] The user submitted the checkout form with payment details.
  // Call trackAddPaymentInfo() from checkout.component.ts submitPayment(), before the API call.
  ADD_PAYMENT_INFO: 'add_payment_info',

  // [Custom] checkoutTransaction() returned success — the payment went through and the order
  // exists, reported from the checkout page itself. Distinct from PURCHASE, which also fires
  // here but is deduplicated identically and mirrored from the summary page as a fallback.
  // Deduplicated per transaction id, so a retried submit is not two sales.
  // Call trackCheckoutCompleted() from checkout.component.ts submitPayment() success.
  CHECKOUT_COMPLETED: 'checkout_completed',

  // [GA4 recommended] checkoutTransaction() succeeded, the order is confirmed.
  // Call trackPurchase() from checkout.component.ts submitPayment() success, and again from
  // summary.component.ts ngOnInit as a fallback for a deep-link/refresh that skips the former.
  // Guard against double-firing: deduplicated on transaction id, first call wins.
  PURCHASE: 'purchase',

  // [Custom] The seat-count reward badge (UpgradesService) crossed its minSeatsToShow
  // threshold upward without yet reaching its goal. Driven by real cart emissions only —
  // not the search-quantity/filters number, and not synthetic (restore/tutorial) emissions.
  // Deduplicated per upgrade per journey (see reportedUpgradeShown), like UPGRADE_ACQUIRED.
  // Client-side-only, never sent to the transaction API, so it is NOT an items[] row,
  // unlike upsell.
  // Call trackUpgradeShown() from upgrades.service.ts checkTotalSeatsForUpgradesMessages().
  UPGRADE_SHOWN: 'upgrade_shown',

  // [Custom] The seat-count reward badge crossed its goal. Deduplicated per upgrade per
  // journey — the 3s toast can re-trigger on further seat changes without this re-firing.
  // Call trackUpgradeAcquired() from upgrades.service.ts checkTotalSeatsForUpgradesMessages().
  UPGRADE_ACQUIRED: 'upgrade_acquired',

  // [Custom] The user clicked a sponsor/partner link on the post-purchase summary page —
  // an outbound link to a third-party domain, so there is no selection state to track,
  // just the click itself. Distinct from group-link (an inbound channel signal).
  // Call trackAdlinkClick() from summary.component.ts click handler.
  ADLINK_CLICK: 'adlink_click',

  // [Custom] Detected centrally off the NavigationEnd screen-resolution logic,
  // not instrumented per cancel call site:
  // every current cancel path (back button, timer expiry, patron-retry, error-modal
  // dismissal) ends in a `checkout` -> `seat_selection` | `events_list` backward navigation.
  TRANSACTION_CANCELLED: 'transaction_cancelled',

  // [Custom] The user tried to submit the checkout form while it still had field-level
  // validation errors. Distinct from CHECKOUT_VALIDATION_ERROR (our hand-written business
  // rules), this one is about Angular control validity. A single submit can fire both.
  // Call trackCheckoutFormInvalid() from each field-level guard in checkout.component.ts
  // submitPayment() — the guard reports its own failing fields alongside its modal.
  CHECKOUT_FORM_INVALID: 'checkout_form_invalid',

  // [Custom] The checkout form was submitted but a client-side business rule rejected it
  // (terms not accepted, no payment method, credit exceeds balance, discount rejected), so
  // no payment was ever attempted. Fired via trackError().
  CHECKOUT_VALIDATION_ERROR: 'checkout_validation_error',

  // [Custom] An API call fired from the checkout flow failed, so the user could not proceed
  // or a checkout-page add-on change was rolled back. `error_type` distinguishes the failing
  // call: 'transaction_create' (createTransaction, the user could not enter checkout),
  // 'upsell_change_failed' (editTransactionBuyerType PATCH), 'rainout_add_failed' /
  // 'rainout_remove_failed' (rainout toggle), 'discount_remove_failed' (discount-code removal).
  // Fired via trackError() from total.component.ts goToCheckout() and from the checkout-page
  // mutation error callbacks in checkout.component.ts / discount-code.component.ts.
  CHECKOUT_ERROR: 'checkout_error',

  // [Custom] DVM seat selection failed: seats no longer available, isolated-seats rejected,
  // or the availability endpoint itself failed. Fired via trackError().
  SEAT_SELECTION_ERROR: 'seat_selection_error',

  // [Custom] The event or its availability failed to load on the seat-selection resolver,
  // so the user cannot select seats at all. Fired via trackError().
  EVENT_LOAD_ERROR: 'event_load_error',

  // [Custom] checkoutTransaction() failed — the payment API returned an error.
  // Fired via trackError() from checkout.component.ts submitPayment() error callback.
  PAYMENT_ERROR: 'payment_error',

  // [Custom] The cart/checkout timer expired before the user completed checkout.
  // Fired via trackError() from checkout.component.ts TimerComponent timeout handler.
  PAYMENT_TIMEOUT: 'payment_timeout',

  // [Custom] The user left the seat-selection page with seats in the cart but never
  // created a transaction, the cart was built and dropped.
  // Fired by trackAbandon({ stage: 'cart' }) from select-seat.component.ts.
  CART_ABANDONED: 'cart_abandoned',

  // [Custom] The user navigated away from checkout with an active (held) transaction.
  // Fired by trackAbandon({ stage: 'payment' }) from checkout.component.ts (ngOnDestroy
  // while on hold, or the pagehide flush when the tab is closed).
  CHECKOUT_ABANDONED: 'checkout_abandoned',

  // [Custom] The user explicitly clicked the header "Back" button on checkout, cancelling
  // the transaction. Suppresses checkout_abandoned for that transaction; also sets the
  // sentinel transaction_cancelled reads as reason: 'checkout_back' once the resulting
  // navigation is detected (the two events are additive, not duplicates.
  // Call trackCheckoutBack() from header.component.ts goBack() — that is the header Back
  // button's actual call site; it calls TransactionService.restartTransaction() directly and
  // never routes through CheckoutComponent.
  CHECKOUT_BACK: 'checkout_back',
} as const;

/** Item list name — first argument to trackViewItemList / trackSelectItem. GS has one list. */
export const ANALYTICS_LIST_NAMES = {
  EVENTS: 'events',
} as const;

/** Item category constants — itemCategory field on AnalyticsItem, GA4's item_category param. */
export const ANALYTICS_ITEM_CATEGORIES = {
  GAME: 'game',
  SEATS: 'seats',
  UPSELL: 'upsell',
  RAINOUT_INSURANCE: 'rainout_insurance',
} as const;

/**
 * Screen name constants used as page_title in page_view events, and to detect the
 * checkout -> seat_selection | events_list backward navigation that fires
 * transaction_cancelled. Resolved automatically from the URL in
 * AnalyticsService.resolveScreenName().
 */
export const ANALYTICS_SCREENS = {
  EVENTS_LIST: 'events_list',
  SEAT_SELECTION: 'seat_selection',
  CHECKOUT: 'checkout',
  CHECKOUT_CONFIRMATION: 'checkout_confirmation',
  UNKNOWN: 'unknown',
} as const;

/**
 * Journey origin values, the entry point the user started the purchase from.
 *
 * Set once per journey by the first trackSelectItem call, read from whether a group link
 * is active at that moment (GroupLinkService.getGroupLink()), and attached as
 * `journey_origin` to every downstream funnel, error and abandonment event.
 */
export const ANALYTICS_JOURNEY_ORIGINS = {
  EVENTS_LIST: 'events_list',
  GROUP_LINK: 'group_link',
} as const;

/** Form name constants, the argument to trackFormStart(). GS has one form: checkout. */
export const ANALYTICS_FORMS = {
  CHECKOUT: 'checkout',
} as const;

/**
 * Owners of a pending abandonment. setPendingAbandon only lets the owner that armed
 * an abandonment clear it: Angular can construct the next route's component before
 * destroying the previous one, so the page being left must not be able to disarm the
 * page being entered.
 */
export const ANALYTICS_ABANDON_OWNERS = {
  CART: 'cart',
  CHECKOUT: 'checkout',
} as const;

/**
 * Best-effort `reason` values for transaction_cancelled, read from whichever local sentinel
 * is set at the moment the checkout -> seat_selection | events_list navigation is detected.
 * Not exhaustive by design: an unenumerated cause still reports, tagged 'unknown', which is
 * a visible signal to add a sentinel at that call site rather than a silent miss (see plan
 * §10 risk 11).
 */
export const ANALYTICS_CANCEL_REASONS = {
  CHECKOUT_BACK: 'checkout_back',
  CART_EXPIRED: 'cart_expired',
  TRANSACTION_NOT_FOUND: 'transaction_not_found',
  PATRON_ASSOCIATION_ERROR: 'patron_association_error',
  UPSELL_CHANGE_ERROR: 'upsell_change_error',
  UNKNOWN: 'unknown',
} as const;

/**
 * Cap on error_message. Backend messages are forwarded verbatim, and anything a backend
 * ever embeds in one (an email, a token) would become personal data sitting in GA4 against
 * Google's terms. Truncating bounds the exposure and the parameter's cardinality.
 */
export const ANALYTICS_MAX_ERROR_MESSAGE_LENGTH = 100;

/** GA4 truncates a parameter value beyond this; better to cut cleanly ourselves. */
export const ANALYTICS_MAX_PARAMETER_VALUE_LENGTH = 100;
